import i18n from "src/i18n/config";
import { getEngagementsBaseUrl } from "src/config/endpoints";
import { getTenantId } from "src/utils/getters";
import { normalizeApiLang } from "src/utils/apiLang";
import { buildAuthHeaders, handleJsonResponse } from "./httpUtils";

const ENGAGEMENTS_BASE_URL = getEngagementsBaseUrl();

export type EngagementMedium = "email" | "whatsapp";

export type EngagementChange = number | "-";

export type EngagementCampaignFilter = string | string[] | null;

interface EngagementEnvelope<TData> {
  status?: string;
  data: TData;
}

export interface EngagementPeriod {
  start_date: string;
  end_date: string;
}

export interface EngagementRangeRequest {
  start_date: string;
  end_date: string;
}

export interface EngagementCampaignNamesRequest extends EngagementRangeRequest {
  medium: EngagementMedium | null;
}

export interface EngagementCampaignNamesData {
  start_date: string;
  end_date: string;
  medium: EngagementMedium | null;
  total_campaign_names: number;
  campaign_names: string[];
}

export interface EngagementFilteredRequest extends EngagementRangeRequest {
  campaign_name: EngagementCampaignFilter;
}

export interface EngagementAchievementMetric {
  value: number | null;
  previous_value?: number | null;
  percent_change?: EngagementChange | null;
}

export interface EngagementAchievementMoneyMetric extends EngagementAchievementMetric {
  formattedValue?: string | null;
  previousFormattedValue?: string | null;
}

export interface EngagementAchievementsData {
  currency?: string | null;
  period?: EngagementPeriod | null;
  comparison_period?: EngagementPeriod | null;
  total_orders?: EngagementAchievementMetric | null;
  total_sales?: EngagementAchievementMoneyMetric | null;
  total_users?: EngagementAchievementMetric | null;
  total_customers?: EngagementAchievementMetric | null;
}

export interface EngagementEmailKpisRequest extends EngagementFilteredRequest {
  refresh?: boolean;
}

export interface EngagementEmailKpisData {
  start_date: string;
  end_date: string;
  comparison_period?: EngagementPeriod | null;

  sent: number;
  opened: number;
  clicks: number;
  conversions: number;
  total_users: number;
  open_rate: number;
  click_through_rate: number;
  bounce_rate: number;

  sent_change?: EngagementChange | null;
  opened_change?: EngagementChange | null;
  clicks_change?: EngagementChange | null;
  conversions_change?: EngagementChange | null;
  total_users_change?: EngagementChange | null;
  open_rate_change?: EngagementChange | null;
  click_through_rate_change?: EngagementChange | null;
  bounce_rate_change?: EngagementChange | null;
}

export interface EngagementWhatsAppKpisData {
  start_date: string;
  end_date: string;
  comparison_period?: EngagementPeriod | null;

  sent: number;
  delivery_rate: number;
  read_rate: number;
  click_rate: number;
  reply_rate: number;
  conversion_rate: number;
  block_rate: number;
  bounce_rate: number;

  sent_change?: EngagementChange | null;
  delivery_rate_change?: EngagementChange | null;
  read_rate_change?: EngagementChange | null;
  click_rate_change?: EngagementChange | null;
  reply_rate_change?: EngagementChange | null;
  conversion_rate_change?: EngagementChange | null;
  block_rate_change?: EngagementChange | null;
  bounce_rate_change?: EngagementChange | null;
}

export interface EngagementMediumRequest extends EngagementFilteredRequest {
  medium: EngagementMedium | null;
  refresh?: boolean;
}

export interface EngagementCampaignPerformanceRow {
  campaign_id: string;
  campaign_name: string;
  medium: EngagementMedium | null;
  sent: number;
  open_rate: number;
  click_rate: number;
  click_through_rate: number;
}

export interface EngagementCampaignPerformanceData {
  start_date: string;
  end_date: string;
  medium: EngagementMedium | null;
  total_campaigns: number;
  campaigns: EngagementCampaignPerformanceRow[];
}

export interface EngagementTimeSeriesRow {
  date: string;
  medium: EngagementMedium | null;
  sent: number;
  opened: number;
  clicks: number;
  open_rate: number;
  click_rate: number;
  click_through_rate: number;
  bounce_rate: number;
}

export interface EngagementTimeSeriesData {
  start_date: string;
  end_date: string;
  medium: EngagementMedium | null;
  series: EngagementTimeSeriesRow[];
}

const postEngagement = async <TData,>(
  path: string,
  body: Record<string, unknown>,
): Promise<TData> => {
  const query = new URLSearchParams({
    lang: normalizeApiLang(i18n.resolvedLanguage ?? i18n.language),
  }).toString();
  const response = await fetch(`${ENGAGEMENTS_BASE_URL}/${path}?${query}`, {
    method: "POST",
    headers: buildAuthHeaders("api_key"),
    // Every engagement endpoint returns localised copy, so `lang` is injected here rather
    // than threaded through each call site. Read from the i18n singleton (not a hook) so
    // it stays correct for calls made outside React and after a language switch.
    body: JSON.stringify({
      tenant_id: Number(getTenantId()),
      ...body,
    }),
  });
  const payload = await handleJsonResponse<EngagementEnvelope<TData>>(response);
  return payload.data;
};

export const engagementOverviewService = {
  fetchCampaignNames: (request: EngagementCampaignNamesRequest) =>
    postEngagement<EngagementCampaignNamesData>("campaign-names", { ...request }),

  fetchAchievements: (request: EngagementFilteredRequest) =>
    postEngagement<EngagementAchievementsData>("datatram-achievements", { ...request }),

  fetchEmailKpis: ({ refresh = false, ...request }: EngagementEmailKpisRequest) =>
    postEngagement<EngagementEmailKpisData>("overview-email-kpis", { ...request, refresh }),

  fetchWhatsAppKpis: (request: EngagementFilteredRequest) =>
    postEngagement<EngagementWhatsAppKpisData>("overview-whatsapp-kpis", { ...request }),

  fetchCampaignPerformance: ({ refresh = false, ...request }: EngagementMediumRequest) =>
    postEngagement<EngagementCampaignPerformanceData>("campaign-performance", {
      ...request,
      refresh,
    }),

  fetchTimeSeries: ({ refresh = false, ...request }: EngagementMediumRequest) =>
    postEngagement<EngagementTimeSeriesData>("time-series", { ...request, refresh }),
};
